import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchCountries,
  fetchSubdivisions,
  createSubdivision,
  updateSubdivision,
  fetchSpecialties,
  createSpecialty,
  updateSpecialty,
  fetchExpertisesForSpecialties,
  createExpertise,
  updateExpertise,
  attachExpertise,
  removeExpertise,
  removeSpecialty,
  type Country,
  type Expertise,
  type Specialty,
  type Subdivision,
  type CreateSubdivisionInput,
  type UpdateSubdivisionInput,
  type CreateSpecialtyInput,
  type CreateExpertiseInput,
  type UpdateNamedInput,
} from '../api/referentialsApi';

const KEY = 'referentials';

/** Invalidates the whole tree, on failure too (a 404 or a race = stale list), before the caller's catch. */
const useReferentialMutation = <TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) => {
  const qc = useQueryClient();
  // No retry: a replayed removal after a 500 could answer 404 and announce a value « gone ».
  return useMutation({ mutationFn, retry: false, onSettled: () => qc.invalidateQueries({ queryKey: [KEY] }) });
};

// Referentials change a handful of times a year: refetching them on every mount is pure noise.
const STALE_TIME = 5 * 60 * 1000;

export const useCountries = () =>
  useQuery<Country[]>({ queryKey: [KEY, 'countries'], queryFn: fetchCountries, staleTime: STALE_TIME });

export const useSubdivisions = () =>
  useQuery<Subdivision[]>({ queryKey: [KEY, 'subdivisions'], queryFn: fetchSubdivisions, staleTime: STALE_TIME });

export const useCreateSubdivision = () =>
  useReferentialMutation((input: CreateSubdivisionInput) => createSubdivision(input));

export const useUpdateSubdivision = () =>
  useReferentialMutation(({ id, data }: { id: number; data: UpdateSubdivisionInput }) => updateSubdivision(id, data));

export const useSpecialties = () =>
  useQuery<Specialty[]>({ queryKey: [KEY, 'specialties'], queryFn: fetchSpecialties, staleTime: STALE_TIME });

export const useCreateSpecialty = () =>
  useReferentialMutation((input: CreateSpecialtyInput) => createSpecialty(input));

export const useUpdateSpecialty = () =>
  useReferentialMutation(({ id, data }: { id: number; data: UpdateNamedInput }) => updateSpecialty(id, data));

/**
 * The cascade the technician record depends on: expertises offered follow the specialties picked.
 * Disabled on an empty selection rather than called with none — the endpoint answers `[]` there,
 * and a query for "nothing" is a round trip whose answer is already known.
 */
export const useExpertisesForSpecialties = (specialtyIds: number[]) =>
  useQuery<Expertise[]>({
    // Sorted in the key so picking A then B and B then A hit the same cache entry.
    queryKey: [KEY, 'expertises', [...specialtyIds].sort((a, b) => a - b)],
    queryFn: () => fetchExpertisesForSpecialties(specialtyIds),
    enabled: specialtyIds.length > 0,
    staleTime: STALE_TIME,
  });

export const useCreateExpertise = () =>
  useReferentialMutation((input: CreateExpertiseInput) => createExpertise(input));

export const useUpdateExpertise = () =>
  useReferentialMutation(({ id, data }: { id: number; data: UpdateNamedInput }) => updateExpertise(id, data));

export const useAttachExpertise = () =>
  useReferentialMutation(({ expertiseId, specialtyId }: { expertiseId: number; specialtyId: number }) =>
    attachExpertise(expertiseId, specialtyId)
  );

export const useRemoveExpertise = () =>
  useReferentialMutation(({ expertiseId, specialtyId }: { expertiseId: number; specialtyId: number }) =>
    removeExpertise(expertiseId, specialtyId)
  );

export const useRemoveSpecialty = () => useReferentialMutation((id: number) => removeSpecialty(id));
